import React, { useEffect, useState, useImperativeHandle, forwardRef, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Button, Alert, Dropdown, Card, Row, Col, Table, InputGroup, Form } from 'react-bootstrap';
import { DatePicker } from "../../Components/datePicker";
import { setLawyerListState, setSelectedLawyer, setSkipSetThirdPartyData } from "../../redux/reducer";
import { RenAIImportFuncs, RenAIImportProps, docHeader, inFileHeader, 
    SectionProps, BoxSectionProps, APSImportSectionProps,
    SectionRecordListProps, SectionRecordErrorMessageProps, SectionRecordNoDataProps, 
    SectionRecordTextProps, SectionRecordDropdownProps, SectionRecordsProps, SectionAlertsProps, SectionHeaderProps, 
    SectionRecordCheckboxProps, SectionRecordDatePickerProps,
    getAPSDisplayShort, getAPSDisplayName,
    checkDatePickerDate} from './renaiFuncs';
import { runInnerFunction, runInnerFunction2 } from '../../compatManager';
import { useAppSelector } from '../../redux/hooks';
import { DocumentDisplay } from '../../Components/MortgageAnalyzer/DocumentDisplay';
import styles from './RenAI.module.scss';
import { getDataReader } from '../../livedataFuncs';
import { FormatDateDataType } from '../../util/genericDateAndTimeFuncs';
import { displayToastMsg } from '../../Components/toastMessages/toastmessages';
import ContactHelp from '../../Components/MortgageAnalyzer/ContactHelp';
import { StepsEnum } from '../../Components/MortgageAnalyzer/DocsDropbox';

export const RenAIImport: React.FC<RenAIImportProps> = (props) => {
    const { show, onHide, responseData, base64Content, LddDocLink, DataReaderObj } = props;
    const docType = responseData?.pdfInfo?.docType;
    const SESSION_INFO = useAppSelector(state => state.SessionInfo);
    const selectedLawyer = useAppSelector(state => state.SelectedLawyer);
    const [allImported, setAllImported] = useState<boolean>(false);
    const [importInfo, setImportInfo] = useState<Record<string, any>>({});
    const [pdfViewer, setPDFViewer] = useState<boolean>(true);
    const [step, setStep] = useState<StepsEnum>(StepsEnum.UploadSuccess);
    const [lastStep, setLastStep] = useState<StepsEnum>(StepsEnum.UploadSuccess);
    const [helpValidFeedback, setHelpValidFeedback] = useState<boolean>(false);
    const contactHelpRef = useRef<HTMLButtonElement>(null);
    const hasImportSelections = useMemo(() => {
        if (!importInfo || typeof importInfo !== 'object') return false;
        return Object.values(importInfo).some((section: any) => section && Object.keys(section as any).length > 0);
    }, [importInfo]);
    const sections = useMemo(() => RenAIImportFuncs.getSections(
        docType, DataReaderObj, responseData, props.newFileCreation), 
        [docType, DataReaderObj, responseData, props.newFileCreation]
    );
    const sectionEditableMasks = useMemo(() => RenAIImportFuncs.getSectionEditableMasks(
        docType, sections), 
        [docType, sections]
    );    
    
    const buyersRef = useRef(null);
    const sellersRef = useRef(null);
    const otherSideRef = useRef(null);
    const propertyRef = useRef(null);
    const priceDepositRef = useRef(null);
    const contractRef = useRef(null);
    const listingAgentRef = useRef(null);
    const coopAgentRef = useRef(null);
    const listingRef = useRef(null);
    const buyerBrokerageRef = useRef(null);
    
    const sectionRefs = {
        buyersRef,
        sellersRef,
        otherSideRef,
        propertyRef,
        priceDepositRef,
        contractRef,
        listingAgentRef,
        coopAgentRef,
        listingRef,
        buyerBrokerageRef
    };
    const APSDisplayName = getAPSDisplayName(SESSION_INFO.appProvinceShort);
    const APSDisplayShort = getAPSDisplayShort(SESSION_INFO.appProvinceShort);

    const handleImportAll = () => {
        let refs = []
        switch(docType.toLowerCase()) {
            case 'aps':
                refs = [sectionRefs.buyersRef, sectionRefs.sellersRef, sectionRefs.otherSideRef, 
                        sectionRefs.propertyRef, sectionRefs.priceDepositRef, sectionRefs.contractRef, 
                        sectionRefs.listingRef, sectionRefs.buyerBrokerageRef];
        }
        refs.forEach(
            ref => ref.current && (
                allImported ? ref.current.handleUndoAll() 
                            : ref.current.handleApplyAll()
            )
        );
        setAllImported(!allImported);
    };

    const enablePDFViewer = () => {
        setPDFViewer(true);
    }

    const disablePDFViewer = () => {
        setPDFViewer(false);
    }

    const handleImport = async () => {
        if (props.newFileCreation) {
            const resp = await fetch(
                `/rplus/${SESSION_INFO.appProvinceShort}/NewLiveFormApp.asp?SessionID=${SESSION_INFO.sid}&lfdata=1&compat=1`
            );
            const respText = await resp.text();
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(respText, "text/xml");
            const tpAppXmlElement = xmlDoc.querySelector('Data > TPAppXML');
            const newDataReaderObj = tpAppXmlElement ? getDataReader(tpAppXmlElement) : null;
            await RenAIImportFuncs.handleImportInfo(
                docType, importInfo, responseData, newDataReaderObj, selectedLawyer, SESSION_INFO
            );
            runInnerFunction("SetPageDirty", null);
            runInnerFunction('CalculateContentHolder', null);
            try{
                if(SESSION_INFO && newDataReaderObj) {
                    await RenAIImportFuncs.logImportInfo(
                        SESSION_INFO, newDataReaderObj?.getAppId(), LddDocLink, importInfo, docType
                    );
                }
            } catch(error){
                console.log("Error logging info", error);
            }
            runInnerFunction('UpdateContentHolder', null);
            runInnerFunction('SaveFile', true);
            displayToastMsg({ id: null, msg: "File created successfully", type: 'success' }, null);

        } else {
            await RenAIImportFuncs.handleImportInfo(
                docType, importInfo, responseData, DataReaderObj, selectedLawyer, SESSION_INFO
            );
            runInnerFunction("SetPageDirty", null);
            runInnerFunction('CalculateContentHolder', null);
            try{
                await RenAIImportFuncs.logImportInfo(
                    SESSION_INFO, DataReaderObj?.getAppId(), LddDocLink, importInfo, docType
                );
            } catch(error){
                console.log("Error logging info", error);
            }
            runInnerFunction('UpdateContentHolder', null);
            runInnerFunction('SaveFile', true);
            displayToastMsg({ id: null, msg: "Information imported successfully", type: 'success' }, null);
        }
        setImportInfo({});
        onHide();
    };

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            size="xl" 
            dialogClassName={pdfViewer? 'display-pdf': ""} centered 
            enforceFocus={false} 
            className={`${pdfViewer ? 'pdf-mode':''}`}
        >
            <div className="modal-content-inner" style={{background: 'var(--bg-30)'}}>
                <Modal.Header>
                    <Modal.Title>
                        {step === StepsEnum.ContactHelp ? (
                            "Help us Improve RenAI"
                        ) : (
                            <>
                                <i className="renai-icon renai-pill"></i>
                                Import {responseData?.result?.docUploadDisplayName}
                            </>
                        )}
                    </Modal.Title>
                    {step !== StepsEnum.ContactHelp && (
                        <div className="ms-auto d-flex">
                            <Button 
                                variant="primary" 
                                className="ms-3" 
                                onClick={pdfViewer ? disablePDFViewer : enablePDFViewer}
                            >
                                <i 
                                    className={
                                        pdfViewer ? 'fa-solid fa-file-pdf' 
                                        : 'fa-regular fa-file-pdf me-2'
                                    }
                                ></i>
                                {pdfViewer ? 'Close PDF Viewer' : 'Open PDF Viewer'}
                            </Button>
                        </div>
                    )}
                </Modal.Header>

                <Modal.Body>
                    {step !== StepsEnum.ContactHelp && (
                        <>
                            <Alert variant="warning" className="mb-1">
                            {`Ensure all information matches with the uploaded ${APSDisplayName}. 
                            Then select the ${APSDisplayShort} information you would like to import.`}
                            <b> Existing information in your file will be overwritten.</b>
                            </Alert>
                            {responseData ? (
                                (() => {
                                    switch (docType?.toLowerCase()) {
                                        case 'aps':
                                            return (
                                                <APSImportSection
                                                    psm={DataReaderObj?.getPSM()} 
                                                    sections={sections} 
                                                    sectionEditableMasks={sectionEditableMasks} 
                                                    sectionRefs={sectionRefs} 
                                                    setImportInfo={setImportInfo} 
                                                    responseData={responseData}
                                                    provinceCode={SESSION_INFO?.appProvinceShort}
                                        />
                                            );
                                        default:
                                            return (<NoDocumentSection />)
                                    }
                                })()
                            ) : (
                                <NoDocumentSection />
                            )}
                        </>
                    )}
                    {step === StepsEnum.ContactHelp && (
                        <ContactHelp
                            ref={contactHelpRef}
                            docID={responseData?.result?.docID || LddDocLink || 'unknown'}
                            fromStep={lastStep}
                            setValidFeedback={setHelpValidFeedback}
                            setStep={setStep}
                            feedbackType="aps"
                        />
                    )}
                </Modal.Body>
                
                <Modal.Footer>
                    <Button 
                        variant="outline-primary" 
                        className="cancel-button" 
                        onClick={() => { 
                            if (step === StepsEnum.ContactHelp) {
                                // Go back to previous step when in ContactHelp
                                setStep(lastStep);
                            } else {
                                // Close entire modal when in main import screen
                                setImportInfo({}); 
                                onHide();
                            }
                        }}
                    >
                        Cancel
                    </Button>

                    {step !== StepsEnum.ContactHelp && (
                        <>
                            <Button 
                                variant="warning" 
                                onClick={() => { setLastStep(step); setStep(StepsEnum.ContactHelp); }}
                            >
                                Need help?
                            </Button>
                            
                            <Button 
                                variant={allImported ? 'outline-secondary' : 'outline-primary'} 
                                onClick={handleImportAll}
                            >
                                {allImported ? 'Deselect All' : 'Select All'}
                            </Button>


                            {
                                responseData && (
                                    <Button 
                                        variant="primary" 
                                        disabled={!hasImportSelections} 
                                        onClick={handleImport}>
                                        { props.newFileCreation ? 'Create File' : 'Import Selected' }
                                    </Button>
                                )
                            }
                        </>
                    )}

                    {step === StepsEnum.ContactHelp && (
                        <Button 
                            variant={helpValidFeedback ? "primary" : "secondary"}
                            disabled={!helpValidFeedback}
                            onClick={() => {
                                if (contactHelpRef.current) {
                                    contactHelpRef.current.click();
                                }
                            }}
                        >
                            Send Feedback
                        </Button>
                    )}
                </Modal.Footer>
            </div>

            {
                pdfViewer && step !== StepsEnum.ContactHelp && (
                    <DocumentDisplay 
                        base64Data={base64Content} 
                        closeViewer={() => {setPDFViewer(false);}} 
                        fileName={responseData?.result?.docUploadDisplayName}
                    />
                )
            }
        </Modal>
    );
};

const NoDocumentSection: React.FC = () => {
    return (
        <div className="text-center py-4">
            <i className={`fas fa-exclamation-triangle text-warning ${styles.warningIcon}`}></i>
            <h5 className="mt-3">No Response Data</h5>
            <p className="text-muted">No analysis data available to display.</p>
        </div>
    )
}

const APSImportSection: React.FC<APSImportSectionProps> = (props) => {
    const showListingAgent = () => {
        switch (props.provinceCode){
            case "ON":
                return props.psm === "S";
            case "NS":
                return props.psm === "S";
            default:
                return true;
        }
    }
    const showCoopAgent = () => {
        switch (props.provinceCode){
            case "ON":
                return props.psm === "P";
            case "NS":
                return props.psm === "S";
            default:
                return true;
        }
    }
    const listingAgentHeaderName = () => {
        switch (props.provinceCode){
            case "AB":
                return "Selling Agent";
            case "NS":
            case "MB":
            case "SK":
                return "Real Estate Agent - Vendor";
            default:
                return "Listing Agent";
        }
    }
    const coopAgentHeaderName = () => {
        switch (props.provinceCode){
            case "AB":
                return "Buying Agent";
            case "BC":
                return "Selling Agent";
            case "NS":
            case "MB":
            case "SK":
                return "Real Estate Agent - Purchaser";
            default:
                return "Co-op/Buyer Agent";
        }
    }
    return (
        <>
            <Section
                ref={props.sectionRefs.buyersRef}
                headers={["Buyer Side", docHeader, inFileHeader]}
                rows={props.sections?.buyers?.fields as string[][]}
                valTypes={props.sections?.buyers?.valTypes as string[][]}
                editableMask={props.sectionEditableMasks?.buyers}
                label='buyers'
                setImportInfo={props.setImportInfo}
            />
            <Section
                ref={props.sectionRefs.sellersRef}
                headers={["Seller Side", docHeader, inFileHeader]}
                rows={props.sections?.sellers?.fields as string[][]}
                valTypes={props.sections?.sellers?.valTypes as string[][]}
                editableMask={props.sectionEditableMasks?.sellers}
                label='sellers'
                setImportInfo={props.setImportInfo}
            />
            {["BC", "NS"].indexOf(props.provinceCode) == -1 && (
                <Section
                    ref={props.sectionRefs.otherSideRef}
                    headers={["Other Side Legal Representation", docHeader, inFileHeader]}
                    rows={props.sections?.otherSideLegalRepresentation?.fields as string[][]}
                    valTypes={props.sections?.otherSideLegalRepresentation?.valTypes}
                    editableMask={props.sectionEditableMasks?.otherSideLegalRepresentation}
                    label='otherSideLegalRepresentation'
                    setImportInfo={props.setImportInfo}
                />
            )}
            <Section
                ref={props.sectionRefs.propertyRef}
                headers={["Property", docHeader, inFileHeader]}
                rows={props.sections?.propertyData?.fields}
                valTypes={props.sections?.propertyData?.valTypes}
                editableMask={props.sectionEditableMasks?.propertyData}
                label='property'
                setImportInfo={props.setImportInfo}
            />
            <Section
                ref={props.sectionRefs.priceDepositRef}
                headers={["Price & Deposits", docHeader, inFileHeader]}
                rows={props.sections?.priceDepositData?.fields}
                valTypes={props.sections?.priceDepositData?.valTypes}
                editableMask={props.sectionEditableMasks?.priceDepositData}
                label='priceDeposits'
                setImportInfo={props.setImportInfo}
            />
            <Section
                ref={props.sectionRefs.contractRef}
                headers={["Contract", docHeader, inFileHeader]}
                rows={props.sections?.contractData?.fields}
                valTypes={props.sections?.contractData?.valTypes}
                editableMask={props.sectionEditableMasks?.contractData}
                label='contract'
                setImportInfo={props.setImportInfo}
            />
            {(showListingAgent()) && (
                <Section
                    ref={props.sectionRefs.listingRef}
                    headers={[listingAgentHeaderName(), docHeader, inFileHeader]}
                    rows={props.sections?.listingAgent?.fields}
                    valTypes={props.sections?.listingAgent?.valTypes}
                    editableMask={props.sectionEditableMasks?.listingAgent}
                    label='listingAgent'
                    setImportInfo={props.setImportInfo}
                />
            )}
            {(showCoopAgent()) && (
                <Section
                    ref={props.sectionRefs.buyerBrokerageRef}
                    headers={[coopAgentHeaderName(), docHeader, inFileHeader]}
                    rows={props.sections?.coopAgent?.fields}
                    valTypes={props.sections?.coopAgent?.valTypes}
                    editableMask={props.sectionEditableMasks?.coopAgent}
                    label='coopAgent'
                    setImportInfo={props.setImportInfo}
                />
            )}

            <Card>
                <Card.Header>
                    <Card.Title>Reference Only</Card.Title>
                </Card.Header>

                <Card.Body>
                    <Row>
                        <Col xs={6}>
                            <BoxSection
                                header="Chattels"
                                items={props.provinceCode === "SK"? []: RenAIImportFuncs.getChattels(props.responseData)}
                            />
                        </Col>
                        
                        <Col xs={6}>
                            <BoxSection
                                header="Fixtures"
                                items={props.provinceCode === "SK"? []: RenAIImportFuncs.getFixtures(props.responseData)}
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </>
    )
}

const Section = forwardRef<any, SectionProps>(
    ({ headers, rows, valTypes, editableMask, label, setImportInfo }, ref) => {
    const dispatch = useDispatch();
    const SESSION_INFO = useAppSelector(state => state.SessionInfo);
    const [noImport, setNoImport] = useState<boolean[]>([]);
    const [appliedRows, setAppliedRows] = useState<boolean[]>([]);
    const [originalValues, setOriginalValues] = useState<string[][]>([]);
    const [inputValues, setInputValues] = useState<string[][]>([]);
    const [allApplied, setAllApplied] = useState<boolean>(false);
    const selectedLawyer = useAppSelector(state => state.SelectedLawyer);
    const [defaultLawyerMatch, setDefaultLawyerMatch] = useState<boolean>(false);
    const [message, setMessage] = useState<string>(null);
    const [isLawyerMatch, setIsLawyerMatch] = useState<boolean>(false);
    const [additionalRows, setAdditionalRows] = useState<string[][]>([]);
    const [additionalRowsValTypes, setAdditionalRowsValTypes] = useState<string[][]>([]);
    const [additionalRowsEditableMask, setAdditionalRowsEditableMask] = useState<boolean[][]>([]);
    const placeholder = (label === 'buyers' || label === 'sellers') ? 
        'eg Smith, John..' 
        : 'no data..';

    const supportsAdditionalRows = label === 'buyers' || label === 'sellers';
    const displayRows = useMemo(() => 
        supportsAdditionalRows ? [...rows, ...additionalRows] : rows, 
        [rows, additionalRows, supportsAdditionalRows]
    );
    
    const displayValTypes = useMemo(() => 
        supportsAdditionalRows ? [...(valTypes || []), ...additionalRowsValTypes] : valTypes, 
        [valTypes, additionalRowsValTypes, supportsAdditionalRows]
    );
    
    const displayEditableMask = useMemo(() => 
        supportsAdditionalRows ? [...editableMask, ...additionalRowsEditableMask] : editableMask, 
        [editableMask, additionalRowsEditableMask, supportsAdditionalRows]
    );

    useEffect(() => {
        setAppliedRows(new Array(rows.length).fill(false));
        setOriginalValues(rows.map(row => [...row]));
        setInputValues(rows.map(row => [...row]));
        setAllApplied(false);
        // reset additional rows if props change
        // this should happen when hitting import so it doesn't display duplicate entries if additional rows were added
        setAdditionalRowsEditableMask([]);
        setAdditionalRowsValTypes([]);
        setAdditionalRows([]);

    }, [rows, label]);

    useEffect(() => {
        const isSupportsAdditional = label === 'buyers' || label === 'sellers';
        const allRows = isSupportsAdditional ? [...rows, ...additionalRows] : rows;
        setAllApplied(false);

        if(isSupportsAdditional){
            let newInputValues = inputValues;
            let newAppliedRows = appliedRows;
            let newOriginalValues = originalValues;
            if(inputValues.length){
                if(additionalRows.length){
                    newInputValues.push(additionalRows[additionalRows.length - 1]);
                    newAppliedRows.push(false);
                    newOriginalValues.push([additionalRows[additionalRows.length - 1][0], "", ""])
                }
            } else {
                newInputValues = allRows.map(row => [...row]);
                newAppliedRows = new Array(allRows.length).fill(false);
                newOriginalValues = allRows.map(row => [...row]);
            }
            setInputValues(newInputValues);
            setAppliedRows(newAppliedRows);
            setOriginalValues(newOriginalValues);
        }
    }, [additionalRows]);

    useEffect(() => {
        if (label === 'otherSideLegalRepresentation' && selectedLawyer && !defaultLawyerMatch) {
            setMessage(null);
            updateInputLawyer(selectedLawyer);
        }
    }, [selectedLawyer])

    useEffect(() => {
        // Ensure arrays are properly aligned before calculating noImport
        if (inputValues.length === 0 || displayEditableMask.length === 0 || 
            inputValues.length !== displayEditableMask.length) {
            setNoImport(new Array(inputValues.length).fill(true));
            return;
        }
        
        const noImportArray = inputValues.map((row, rowIndex) => {
            if (!displayEditableMask[rowIndex] || !displayValTypes?.[rowIndex]) {
                return true; 
            }
            
            // if (supportsAdditionalRows && rowIndex >= rows.length) {
            //     return false;
            // }
            
            return row.some((cell, cellIndex) => {
                if (!displayEditableMask[rowIndex][cellIndex]) 
                    return false;
                const valType = displayValTypes[rowIndex][cellIndex] || 'string';
                if (valType === 'checkbox') 
                    return false;
                return !cell || cell === '' || 
                (
                    valType === 'date' ? 
                    cell === placeholder : 
                    cell.trim() === ''
                );
            });
        });
        setNoImport(noImportArray);
    }, [inputValues, displayEditableMask, displayValTypes, placeholder, supportsAdditionalRows, rows]);

    useImperativeHandle(ref, () => ({
        handleApplyAll,
        handleUndoAll
    }));

    const updateInputLawyer = async (lawyer: any) => {
        if (!lawyer) return;

        const { Name, Address, DirectPhone } = lawyer;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(lawyer.XMLData, "text/xml");
        const solicitorElement = xmlDoc.getElementsByTagName("Solicitor")[0];
        const FirmEmail = solicitorElement?.getAttribute("FirmEmail");
        const InputFax = solicitorElement?.getAttribute("Fax");
        // Alberta DB seems to have some lawyers with text in the fax field like "Fax (xxx)-..."
        const Fax = InputFax.replace(/[a-zA-Z]/g, "").trim();
        setInputValues(prev => {
            const updatedValues = [...prev];
            if(Name && Name !== '') {
                updatedValues[0][1] = Name;
            }
            if(Address && Address !== '') {
                updatedValues[1][1] = Address;
            }
            if(DirectPhone && DirectPhone !== '') {
                updatedValues[2][1] = DirectPhone;
            }
            if(FirmEmail && FirmEmail !== '') {
                updatedValues[3][1] = FirmEmail;
            }
            if(Fax && Fax !== '') {
                updatedValues[4][1] = Fax;
            }
            return updatedValues;
        });

        await handleLawyerMatch();
    };

    const handleLawyerMatch = async (defaultMatch?: boolean) => {
        if (label === 'otherSideLegalRepresentation' && inputValues.length > 0) {
            const isMatch = await RenAIImportFuncs.matchLawyer(
                SESSION_INFO, inputValues[0][1], inputValues[3][1]
            );
            setIsLawyerMatch(isMatch?.ok);
            if(isMatch?.ok && isMatch?.result){
                if(defaultMatch){
                    setDefaultLawyerMatch(true);
                    dispatch(setSelectedLawyer(isMatch?.result));
                } else {
                    setDefaultLawyerMatch(false);
                }
            }
        }
    };

    const addNewRow = () => {
        if (label === 'buyers' || label === 'sellers') {
            if (rows.length > 0) {
                const templateRow = rows[0];
                const templateValTypes = valTypes?.[0] || [];
                const templateEditableMask = editableMask[0];
                const newRow = templateRow.map((_, index) => {
                    if (index === 0) {
                        return `${label === 'buyers' ? 'Buyer' : 'Seller'} ${additionalRows.length + rows.length + 1}`;
                    }
                    return '';
                });

                const newValTypes = [...templateValTypes];
                const newEditableMask = [...templateEditableMask];

                setAdditionalRows(prev => [...prev, newRow]);
                setAdditionalRowsValTypes(prev => [...prev, newValTypes]);
                setAdditionalRowsEditableMask(prev => [...prev, newEditableMask]);
            }
        }
    };

    const SectionAlerts: React.FC<SectionAlertsProps> = (props) => {
        return (
            <>
                {!hasVisibleCheckboxes && (
                    <Alert variant="danger" className="mb-3 mt-1 alert-text">
                        <b>No information detected.</b> {
                            props.label === 'otherSideLegalRepresentation' 
                                ? 'Choose a lawyer to import information.' 
                                : 'Enter any details that you wish to import.'
                        }
                    </Alert>
                )}

                {props.label === 'otherSideLegalRepresentation' && !props.isLawyerMatch && !selectedLawyer && hasVisibleCheckboxes && (
                    <Alert variant="danger" className="mb-3 mt-1 alert-text">
                        Lawyer not found in your database. Choose a lawyer or import limited data.
                    </Alert>
                )}
                {
                    props.label === 'otherSideLegalRepresentation' && props.isLawyerMatch && (
                        <Alert variant="warning" className="mb-3">
                            RenAI attempted to match the lawyer information from your Lawyer database. This information may not reflect the most up to date information.
                        </Alert>
                    )
                }
            </>
        );
    };

    const SectionHeader: React.FC<SectionHeaderProps> = (props) => {
        return (
            <thead>
                <tr>
                    {props.headers.map((header, index) => (
                        <th key={index}>{header}</th>
                    ))}
                    
                    {props.hasVisibleCheckboxes && (
                        <th className="text-end">
                            <label className={`form-check-label ${styles.selectAllLabel}`}>
                                Select All
                            </label>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={props.allApplied}
                                onChange={(e) => props.onSelectAllChange(e.target.checked)}
                            />
                        </th>
                    )}
                </tr>
            </thead>
        );
    };

    const handleInputChange = (rowIndex: number, cellIndex: number, value: string) => {
        const newInputValues = [...inputValues];
        newInputValues[rowIndex][cellIndex] = value;
        if (appliedRows[rowIndex]) {
            const editableColumnIndex = displayEditableMask[rowIndex].findIndex(mask => mask);
            if (editableColumnIndex === cellIndex) {
                newInputValues[rowIndex][newInputValues[rowIndex].length - 1] = value;
                const firstColumnText = newInputValues[rowIndex][0];
                setImportInfo((prev: Record<string, any>) => ({
                    ...prev,
                    [label]: { ...(prev[label] || {}), [firstColumnText]: value }
                }));
            }
        }
        setInputValues(newInputValues);
    };

    const handleFieldUndo = (rowIndex: number, cellIndex: number) => {
        const newInputValues = [...inputValues];
        const originalValue = originalValues[rowIndex][cellIndex];
        newInputValues[rowIndex][cellIndex] = originalValue;
        if (appliedRows[rowIndex]) {
            const editableColumnIndex = displayEditableMask[rowIndex].findIndex(mask => mask);
            if (editableColumnIndex === cellIndex) {
                newInputValues[rowIndex][newInputValues[rowIndex].length - 1] = originalValue;
                const firstColumnText = newInputValues[rowIndex][0];
                setImportInfo((prev: Record<string, any>) => ({
                    ...prev,
                    [label]: { ...(prev[label] || {}), [firstColumnText]: originalValue }
                }));
            }
        }
        setInputValues(newInputValues);
    };

    const isValueEdited = (rowIndex: number, cellIndex: number) => {
        if (!originalValues[rowIndex] || originalValues[rowIndex][cellIndex] === "" || !displayEditableMask[rowIndex][cellIndex]) return false;
        const currentValue = inputValues[rowIndex][cellIndex];
        const originalValue = originalValues[rowIndex][cellIndex];
        const valType = displayValTypes?.[rowIndex]?.[cellIndex] || 'string';
        if (valType === 'checkbox') {
            const currentBool = String(currentValue).toLowerCase() === 'true';
            const originalBool = String(originalValue).toLowerCase() === 'true';
            return currentBool !== originalBool;
        } else if (valType === 'date') {
            const normalizeDate = (value: any) => {
                if (!value || value === '' || value === placeholder) return '';
                return String(value);
            };
            const normalizedCurrent = normalizeDate(currentValue);
            const normalizedOriginal = normalizeDate(originalValue);
            return normalizedCurrent !== normalizedOriginal;
        }
        return currentValue !== originalValue;
    };

    const handleApply = (rowIndex: number) => {
        const newInputValues = [...inputValues];
        const newAppliedRows = [...appliedRows];
        const editableColumnIndex = displayEditableMask[rowIndex].findIndex(mask => mask);
        if (editableColumnIndex !== -1) {
            let editableValue = inputValues[rowIndex][editableColumnIndex];
            const valType = displayValTypes?.[rowIndex]?.[editableColumnIndex] || 'string';
            if (valType === 'money' && editableValue) {
                editableValue = runInnerFunction2("FormatMoneyDataType", [editableValue, 'delimited']);
                newInputValues[rowIndex][editableColumnIndex] = editableValue;
            }
            
            newInputValues[rowIndex][newInputValues[rowIndex].length - 1] = editableValue;
            newAppliedRows[rowIndex] = true;
            setInputValues(newInputValues);
            setAppliedRows(newAppliedRows);
            const firstColumnText = newInputValues[rowIndex][0];
            const lastColumnText = newInputValues[rowIndex][newInputValues[rowIndex].length - 1];
            setImportInfo((prev: Record<string, any>) => ({
                ...prev,
                [label]: { ...(prev[label] || {}), [firstColumnText]: lastColumnText }
            }));
            const allRowsApplied = newAppliedRows.every((applied, idx) => 
                applied || (noImport[idx] !== undefined ? noImport[idx] : true)
            );
            setAllApplied(allRowsApplied);
        }
    };

    const handleUndo = (rowIndex: number) => {
        const newInputValues = [...inputValues];
        const newAppliedRows = [...appliedRows];    
        newInputValues[rowIndex][newInputValues[rowIndex].length - 1] = originalValues[rowIndex][originalValues[rowIndex].length - 1];
        newAppliedRows[rowIndex] = false;
        setInputValues(newInputValues);
        setAppliedRows(newAppliedRows);
        setAllApplied(false);
        const firstColumnText = newInputValues[rowIndex][0];
        setImportInfo((prev: Record<string, any>) => {
            const newImportInfo = { ...prev };
            if (newImportInfo[label]) {
                delete newImportInfo[label][firstColumnText];
                if(Object.keys(newImportInfo[label]).length === 0) {
                    delete newImportInfo[label];
                }
            }
            return newImportInfo;
        });
    };

    const handleApplyAll = () => {
        const newInputValues = [...inputValues];
        const newAppliedRows = [...appliedRows];
        const newImportData: Record<string, string> = {};
        displayRows.forEach((row, rowIndex) => {
            if (!noImport[rowIndex]) {
                const editableColumnIndex = displayEditableMask[rowIndex].findIndex(mask => mask);
                if (editableColumnIndex !== -1) {
                    let editableValue = inputValues[rowIndex][editableColumnIndex];
                    const valType = displayValTypes?.[rowIndex]?.[editableColumnIndex] || 'string';
                    if (valType === 'money' && editableValue) {
                        editableValue = runInnerFunction2("FormatMoneyDataType", [editableValue, 'delimited']);
                        newInputValues[rowIndex][editableColumnIndex] = editableValue;
                    }
                    
                    newInputValues[rowIndex][newInputValues[rowIndex].length - 1] = editableValue;
                    newAppliedRows[rowIndex] = true;
                    const firstColumnText = newInputValues[rowIndex][0];
                    const lastColumnText = newInputValues[rowIndex][newInputValues[rowIndex].length - 1];
                    newImportData[firstColumnText] = lastColumnText;
                }
            }
        });
        
        setInputValues(newInputValues);
        setAppliedRows(newAppliedRows);
        setAllApplied(true);
        
        setImportInfo((prev: Record<string, any>) => ({
            ...prev,
            [label]: { ...(prev[label] || {}), ...newImportData }
        }));
    };

    const handleUndoAll = () => {
        const newInputValues = [...inputValues];
        const newAppliedRows = [...appliedRows];
        displayRows.forEach((row, rowIndex) => {
            newInputValues[rowIndex][newInputValues[rowIndex].length - 1] = 
            originalValues[rowIndex][originalValues[rowIndex].length - 1];
            newAppliedRows[rowIndex] = false;
        });
        
        setInputValues(newInputValues);
        setAppliedRows(newAppliedRows);
        setAllApplied(false); 
        setImportInfo((prev: Record<string, any>) => {
            const newImportInfo = { ...prev };
            delete newImportInfo[label];    
            return newImportInfo;
        });
    };

    const hasVisibleCheckboxes = noImport.some(noImportValue => !noImportValue);

    return (
        <Card>
            <Card.Body>
                <SectionAlerts 
                    hasVisibleCheckboxes={hasVisibleCheckboxes}
                    headerTitle={headers[0]}
                    label={label}
                    isLawyerMatch={isLawyerMatch}
                />

                <Table borderless className={styles.importTable}>
                    <SectionHeader 
                        headers={headers}
                        hasVisibleCheckboxes={hasVisibleCheckboxes}
                        allApplied={allApplied}
                        onSelectAllChange={(checked) => checked ? handleApplyAll() : handleUndoAll()}
                    />

                    <SectionRecords
                        rows={displayRows}
                        inputValues={inputValues}
                        valTypes={displayValTypes}
                        editableMask={displayEditableMask}
                        isValueEdited={isValueEdited}
                        handleInputChange={handleInputChange}
                        handleFieldUndo={handleFieldUndo}
                        handleLawyerMatch={handleLawyerMatch}
                        noImport={noImport}
                        appliedRows={appliedRows}
                        handleApply={handleApply}
                        handleUndo={handleUndo}
                        label={label}
                        message={message}
                        styles={styles}
                        placeholder={placeholder}
                        provinceCode={SESSION_INFO?.appProvinceShort}
                    />
                    
                    {false && (label === 'buyers' || label === 'sellers') && (
                        <tfoot>
                            <tr>
                                <td>
                                    <Button 
                                        variant="link-text"
                                        onClick={addNewRow}
                                    >
                                        <i className="fas fa-plus me-1"></i>
                                        Add
                                        </Button>
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </Table>
            </Card.Body>
        </Card>
    );
});


const SectionRecords: React.FC<SectionRecordsProps> = (props) => {
    if (!props.rows || props.rows.length === 0) {
        return <div className="text-center text-muted p-4 border-top">No data available</div>;
    }

    const visibilityStatus = (record: SectionRecordsProps, rowIndex: number) => {
        if (record.label === 'property' && 
            !record?.inputValues[7][1]?.toLowerCase().includes('condo') && 
            (rowIndex === 0 || rowIndex === 1)) {
            record.appliedRows[rowIndex] = false;
            return false;
        }
        switch (record.provinceCode) {
            case "AB":
                if ((record.label === "property" && rowIndex == 7) ||
                    (record.label === "contract" && rowIndex == 2)) {
                    record.appliedRows[rowIndex] = false;
                    return false;
                }
                break;
            case "BC":
                if (record.label === "property" && (rowIndex == 7 || rowIndex == 3)) {
                    record.appliedRows[rowIndex] = false;
                    return false;
                }
                // Hide phone number row (index 2) for agent sections in BC
                if ((record.label === "listingAgent" || record.label === "coopAgent") && rowIndex == 2) {
                    record.appliedRows[rowIndex] = false;
                    return false;
                }
                break;
            case "NS":
                if ((record.label === "property" && rowIndex == 7) ||
                    (record.label === "contract" && rowIndex == 2) ||
                    (record.label === "listingAgent" && rowIndex == 2) ||
                    (record.label === "coopAgent" && rowIndex == 2) ||
                    (record.label === "priceDeposits" && rowIndex == 1)) {
                    record.appliedRows[rowIndex] = false;
                    return false;
                }
                break;
            case "MB":
                if((record.label === "contract" && rowIndex == 1) ||
                   (record.label === "otherSideLegalRepresentation") && rowIndex == 4){
                    record.appliedRows[rowIndex] = false;
                    return false
                }
                break;
            case "SK":
                if((record.label === "contract" && rowIndex === 2) ||
                   (record.label === "property" && rowIndex === 7) ||
                   (record.label === "priceDeposits") && rowIndex == 1){
                    record.appliedRows[rowIndex] = false;
                    return false
                }
        }
        return true;
    }

    const getPropertyTypeOptions = () => {
        switch (props.provinceCode) {
            case "AB":
                return ["Freehold (Fee Simple)", "Condo", "Leasehold", "Subleasehold", "CondoSubLeasehold", ""];
            default:
                return ["Freehold (Fee Simple)", "Condo"];
        }
    }

    return (
        <tbody>
            {props.inputValues.map((row, rowIndex) => (
                visibilityStatus(props, rowIndex) && (
                <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => {
                        const valType = props.valTypes?.[rowIndex]?.[cellIndex] || 'string';
                        const isEditable = props.editableMask[rowIndex][cellIndex];
                        const hasBeenEdited = props.isValueEdited(rowIndex, cellIndex);
                        
                        return (
                            <td key={cellIndex}>
                                {isEditable ? (
                                    valType === 'checkbox' ? (
                                        <SectionRecordCheckbox
                                            checked={String(props.inputValues[rowIndex][cellIndex]) === 'true'}
                                            onChange={(checked) => props.handleInputChange(rowIndex, cellIndex, checked.toString())}
                                            disabled={false}
                                        />
                                    ) : valType === 'date' ? (
                                        <SectionRecordDatePicker
                                            key={`${rowIndex}-${cellIndex}-${props.inputValues[rowIndex][cellIndex]}`}
                                            date={checkDatePickerDate(props.inputValues[rowIndex][cellIndex]) || ''} // validate date
                                            setDate={(newDate) => props.handleInputChange(rowIndex, cellIndex, newDate || '')}
                                            isEdited={hasBeenEdited}
                                            onUndo={() => props.handleFieldUndo(rowIndex, cellIndex)}
                                        />
                                    ) : valType === 'lawyerlist' ? (
                                        <SectionRecordList
                                            inputValues={props.inputValues}
                                            rowIndex={rowIndex}
                                            cellIndex={cellIndex}
                                            cell={cell}
                                            hasBeenEdited={hasBeenEdited}
                                            handleInputChange={props.handleInputChange}
                                            handleLawyerMatch={props.handleLawyerMatch}
                                            handleFieldUndo={props.handleFieldUndo}
                                            placeholder={props.placeholder}
                                        />
                                    ) : valType === 'dropdown'? (
                                        <SectionRecordDropdown
                                            inputValues={props.inputValues}
                                            rowIndex={rowIndex}
                                            cellIndex={cellIndex}
                                            cell={cell}
                                            hasBeenEdited={hasBeenEdited}
                                            handleInputChange={props.handleInputChange}
                                            handleFieldUndo={props.handleFieldUndo}
                                            placeholder={props.placeholder}
                                            options={getPropertyTypeOptions()}
                                        />
                                    )
                                    :(
                                        <SectionRecordText
                                            inputValues={props.inputValues}
                                            rowIndex={rowIndex}
                                            cellIndex={cellIndex}
                                            cell={cell}
                                            hasBeenEdited={hasBeenEdited}
                                            handleInputChange={props.handleInputChange}
                                            handleFieldUndo={props.handleFieldUndo}
                                            placeholder={props.placeholder}
                                            valType={valType}
                                            label={props.label}
                                            handleLawyerMatch={props.handleLawyerMatch}
                                        />
                                    )
                                ) : (
                                    valType === 'checkbox' ? (
                                        <SectionRecordCheckbox
                                            checked={String(cell) === 'true'}
                                            disabled={true}
                                        />
                                    ) : (
                                        <SectionRecordNoData
                                            cell={cell}
                                            styles={styles}
                                            placeholder={'no data..'}
                                            valType={valType}
                                            label={props.label}
                                        />
                                    )
                                )}
                                <SectionRecordErrorMessage
                                    rowIndex={rowIndex}
                                    cellIndex={cellIndex}
                                    label={props.label}
                                    message={props.message}
                                />
                            </td>
                        );
                    })}
                    {!props.noImport[rowIndex] &&
                        <td className="text-end">
                            <SectionRecordCheckbox
                                checked={props.appliedRows[rowIndex]}
                                onChange={(checked) => checked ? props.handleApply(rowIndex) : props.handleUndo(rowIndex)}
                                disabled={false}
                                end={true}
                            />
                        </td>
                    }
                </tr>
                )
            ))}
        </tbody>
    );
};

const SectionRecordText: React.FC<SectionRecordTextProps> = (props) => {
    return (
        <InputGroup className={`${props.hasBeenEdited ? styles.edited : styles.unedited}`}>
            <Form.Control
                type="text"
                value={typeof props.inputValues[props.rowIndex][props.cellIndex] === 'object' && props.inputValues[props.rowIndex][props.cellIndex] !== null 
                    ? JSON.stringify(props.inputValues[props.rowIndex][props.cellIndex]) 
                    : String(props.inputValues[props.rowIndex][props.cellIndex] || '')
                }
                placeholder={props.cell === '' ? props.placeholder : ''}
                onChange={(e) => props.handleInputChange(props.rowIndex, props.cellIndex, e.target.value)}
                onBlur={(e) => {
                    if (props.valType === 'money') {
                        e.target.value = runInnerFunction2("FormatMoneyDataType", [e.target.value, 'delimited']);
                    }
                    if (props.label === 'otherSideLegalRepresentation') {
                        props.handleLawyerMatch();
                    }
                }}
            />
            {/* <i 
                className={`fa-regular fa-pen ${styles.hoverIcon} ${props.hasBeenEdited ? styles.editWithUndo : styles.edit}`}
                onClick={(e) => {
                    const input = (e.currentTarget as HTMLElement).parentElement?.querySelector('input') as HTMLInputElement;
                    if (input) input.focus();
                }}
                title="Edit field"
            /> */}
            {props.hasBeenEdited && (
                <InputGroup.Text className={styles.hoverIcon}>
                    <i 
                        className="fa fa-undo"
                        onClick={() => props.handleFieldUndo(props.rowIndex, props.cellIndex)}
                        title="Undo changes"
                    />
                </InputGroup.Text>
            )}
        </InputGroup>
    );
};

const SectionRecordDropdown: React.FC<SectionRecordDropdownProps> = (props) => {
    const currentValue = String(props.inputValues[props.rowIndex][props.cellIndex] || '');
    // const displayValue = currentValue || props.placeholder;
    const displayValue = currentValue;
    const displayNameMap = {"Condo": "Condominium (Freehold)", "CondoSubLeasehold": "Condominium (Subleasehold)", "": "No type of Property"}
    
    return (
        <Dropdown>
            <Dropdown.Toggle as="div" style={{ visibility: 'visible' }}
            >
                <InputGroup className={`${props.hasBeenEdited ? styles.edited : styles.unedited}`}>
                    <Form.Control
                        value={displayNameMap[displayValue] || displayValue}
                        style={{ pointerEvents: 'none', backgroundColor: 'white' }}
                        tabIndex={-1}
                        onChange={(e) => props.handleInputChange(props?.rowIndex, props?.cellIndex, e.target.value)}
                    />

                    {props.hasBeenEdited && (
                        <InputGroup.Text className={styles.hoverIcon}>
                            <i 
                                className="fa fa-undo"
                                onClick={() => props.handleFieldUndo(props.rowIndex, props.cellIndex)}
                                title="Undo changes"
                            />
                        </InputGroup.Text>
                    )}
                </InputGroup>
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {props.options.map((option, index) => (
                    <Dropdown.Item
                        key={index}
                        onClick={() => props.handleInputChange(props.rowIndex, props.cellIndex, option)}
                        active={currentValue === option}
                    >
                        {displayNameMap[option] || option}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
};

const SectionRecordNoData: React.FC<SectionRecordNoDataProps> = (props) => {
    const formatDisplayValue = (cell: any) => {
        if (cell === '' || cell === props.placeholder) {
            return props.placeholder;
        }

        if (props.valType === 'date' && typeof cell === 'string' && cell) {
            const parsedDate = FormatDateDataType(new Date(cell).toISOString(), 'dfca');
            return parsedDate ? parsedDate : String(cell);
        }
        if (props.valType === 'money' && typeof cell === 'string' && cell) {
            const parsedMoney = runInnerFunction2("FormatMoneyDataType", [cell, 'delimited']);
            return parsedMoney ? parsedMoney : String(cell);
        }

        if (typeof cell === 'object' && cell !== null) {
            return JSON.stringify(cell);
        }

        if (props.label === 'property' && props.cell === 'Condo') {
            return 'Condominium (Freehold)';
        }

        return String(cell || '');
    };

    return (
        <span
            className={props.cell === '' || props.cell === props.placeholder ? "placeholder-text" : ''}
        >
            {formatDisplayValue(props.cell)}
        </span>
    );
};

const SectionRecordErrorMessage: React.FC<SectionRecordErrorMessageProps> = (props) => {
    if (props.rowIndex === 0 && props.cellIndex === 1 && props.label === 'otherSideLegalRepresentation' && props.message) {
        return (
            <div className="text-danger">
                {props.message}
            </div>
        );
    }
    return null;
};

const BoxSection: React.FC<BoxSectionProps> = (props) => {
    return (
        <>
            <Card.Title>{props.header}</Card.Title>
            {props.items && props.items.length > 0 ? (
                <p>
                    {props.items.join(', ')}
                </p>
            ) : (
                <p>
                    No data available
                </p>
            )}
        </>
    );
}; 

const SectionRecordList: React.FC<SectionRecordListProps> = (props) => {
    const dispatch = useDispatch();
    
    useEffect(() => {
        props.handleLawyerMatch(true);
    }, []);
    
    return (
        <InputGroup className={`${props.hasBeenEdited ? styles.edited : styles.unedited}`}>
            <Form.Control
                type="text"
                value={typeof props.inputValues[props.rowIndex][props.cellIndex] === 'object' && props.inputValues[props.rowIndex][props.cellIndex] !== null 
                    ? JSON.stringify(props.inputValues[props.rowIndex][props.cellIndex]) 
                    : String(props.inputValues[props.rowIndex][props.cellIndex] || '')
                }
                placeholder={props.cell === '' ? props.placeholder : ''}
                onChange={async (e) => {
                    props.handleInputChange(props.rowIndex, props.cellIndex, e.target.value);
                }}
                onBlur={async () => {
                    await props.handleLawyerMatch();
                }}
            />

            {props.hasBeenEdited && (
                <InputGroup.Text className={styles.hoverIcon}>
                    <i 
                        className="fa fa-undo"
                        onClick={() => {
                            props.handleFieldUndo(props.rowIndex, props.cellIndex);
                            props.handleLawyerMatch();
                        }}
                        title="Undo changes"
                    />
                </InputGroup.Text>
            )}

            <InputGroup.Text>
                <i 
                    className="fa-solid fa-list"
                    onClick={() => {
                        dispatch(setLawyerListState(true));
                        dispatch(setSkipSetThirdPartyData(true));
                    }}
                    title="Select from lawyer list"
                />
            </InputGroup.Text>
        </InputGroup>
    );
};

const SectionRecordCheckbox: React.FC<SectionRecordCheckboxProps> = (props) => {
    return (
        <div className={styles.checkboxSpacing} style={props.end?{justifyContent: 'flex-end'}:{}}>
            <input
                type="checkbox"
                className="form-check-input"
                checked={props.checked}
                onChange={
                    props.onChange ? (e) => 
                        props.onChange(e.target.checked) : undefined
                }
                disabled={props.disabled}
            />
        </div>      
    );
};

const SectionRecordDatePicker: React.FC<SectionRecordDatePickerProps> = (props) => {
    if (props.disabled) {
        return (
            <span className={styles.disabledDate}>
                {props.date}
            </span>
        );
    }
    return (
        <DatePicker
            date={props.date}
            setDate={props.setDate}
            placeholder={props.placeholder}
            isEdited={props.isEdited}
            onUndo={props.isEdited ? props.onUndo : undefined}
        />
    );
};